import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { X, Camera, WarningCircle } from "@phosphor-icons/react";

/* Reusable camera QR scanner. Scans once, calls onScan(text) then closes. */
export default function QrScannerModal({ open, title, onScan, onClose }) {
    const elId = useRef(`qr-reader-${Math.random().toString(36).slice(2)}`);
    const scannerRef = useRef(null);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        const inst = new Html5Qrcode(elId.current);
        scannerRef.current = inst;
        inst.start(
            { facingMode: "environment" },
            { fps: 10, qrbox: 230 },
            (decodedText) => {
                if (cancelled) return;
                cancelled = true;
                inst.stop().then(() => inst.clear()).catch(() => {});
                onScan(decodedText);
            },
            () => { /* per-frame decode miss — ignore */ }
        ).catch((e) => {
            setError("Couldn't access the camera. Use the manual option below instead.");
        });

        return () => {
            cancelled = true;
            try { inst.stop().then(() => inst.clear()).catch(() => {}); } catch {}
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    if (!open) return null;
    return (
        <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4" data-testid="qr-scanner-modal">
            <div className="bg-white rounded-lg w-full max-w-sm shadow-xl overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--rom-border)]">
                    <h3 className="font-display font-bold text-base flex items-center gap-2"><Camera size={16} weight="bold" /> {title || "Scan QR code"}</h3>
                    <button onClick={onClose} data-testid="qr-scanner-close" className="p-1 text-[var(--rom-text-2)] hover:text-[var(--rom-text)]"><X size={18} /></button>
                </div>
                <div className="p-3">
                    {error ? (
                        <div className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-[6px] p-3" data-testid="qr-scanner-error">
                            <WarningCircle size={16} weight="bold" className="shrink-0 mt-0.5" /> {error}
                        </div>
                    ) : (
                        <div id={elId.current} className="w-full rounded overflow-hidden" data-testid="qr-scanner-viewport" />
                    )}
                    <p className="text-xs text-[var(--rom-text-3)] mt-2 text-center">Point the camera at the QR code to scan automatically.</p>
                </div>
            </div>
        </div>
    );
}
